import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  siteName?: string;
  canonical?: string;
  structuredData?: object;
}

const defaultProps: Required<SEOProps> = {
  title: 'Cycling Buddy - Fahrrad Routenplaner | Rennrad, Gravel, MTB',
  description: 'Cycling Buddy - Dein intelligenter Fahrrad-Routenplaner. Plane perfekte Routen mit detailliertem Höhenprofil für Rennrad, Gravel, MTB und mehr.',
  keywords: 'fahrrad, routenplaner, cycling, bike, rennrad, mtb, gravel, höhenprofil, navigation, route planning, bicycle',
  image: 'https://cycling-buddy.app/android-chrome-512x512.png',
  url: 'https://cycling-buddy.app',
  type: 'website',
  siteName: 'Cycling Buddy',
  canonical: '',
  structuredData: {}
};

export const SEO: React.FC<SEOProps> = (props) => {
  const {
    title,
    description,
    keywords,
    image,
    url,
    type,
    siteName,
    canonical,
    structuredData
  } = { ...defaultProps, ...props };

  // Ensure title is properly formatted
  const pageTitle = title === defaultProps.title 
    ? title 
    : `${title} | Cycling Buddy`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Cycling Buddy" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="German" />
      <meta name="revisit-after" content="7 days" />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="de_DE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional Meta Tags for SEO */}
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Geo Location (relevant for cycling app) */}
      <meta name="geo.region" content="DE" />
      <meta name="geo.placename" content="Germany" />
      <meta name="ICBM" content="51.1657, 10.4515" />

      {/* Theme color */}
      <meta name="theme-color" content="#4a7c2a" />
      <meta name="msapplication-TileColor" content="#4a7c2a" />

      {/* Structured Data */}
      {Object.keys(structuredData).length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;